import React, { useState } from 'react';
import DefaultSuccess from '@/assets/img/default-success.svg';
import { CustomTextField } from '@/components/Formik/CustomTextField';
import { IAddressData } from '@/components/Modals/ConfirmAddress';
import ModalDefault from '@/components/Modals/ModalDefault';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import { handleErrorAsync } from '@/utils/handlers';
import { KeyboardBackspace } from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  Modal,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Button,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

interface IConfirmNewAddressProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
  newDataAddress: IAddressData;
}

export default function ConfirmNewAddress({
  isOpen,
  setIsOpen,
  onSuccess,
  newDataAddress,
}: IConfirmNewAddressProps) {
  const { user, updateUserData } = useUser();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { setIsLoading } = useLoading();

  const [openModalConfirmAddressSuccess, setOpenConfirmAddressSuccess] =
    useState(false);

  const progress = 95;

  const handleValidateAddress = async (values: Partial<IAddressData>) => {
    const newDataAddressUpdated = {
      ...newDataAddress,
      number: values.number,
      complement: values.complement,
      logo: undefined,
      has_confirmed_address: true,
    };

    const [companyUpdated, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      const { data } = await api.put<{ data: ICompany }>(
        `/api/companies/${newDataAddressUpdated.companyId}/`,
        newDataAddressUpdated
      );

      return data;
    }).finally(() => setIsLoading(false));

    if (error) {
      Toast(
        'error',
        error.message || 'Ocorreu um erro inesperado. Tente novamente!'
      );
      return;
    }

    updateUserData({
      memberships: user.memberships?.map(membership =>
        membership.company.id === newDataAddressUpdated?.companyId
          ? {
              ...membership,
              company: Object.assign(membership.company, companyUpdated),
            }
          : membership
      ),
    });

    setOpenConfirmAddressSuccess(true);
  };

  const handleCloseModalSuccess = () => {
    setOpenConfirmAddressSuccess(false);
    setIsOpen(false);
    onSuccess();
  };

  const formValidationSchema = yup.object().shape({
    number: yup
      .string()
      .max(255, 'O número não pode ter mais de 255 caracteres'),
    complement: yup
      .string()
      .max(255, 'O complemento não pode ter mais de 255 caracteres'),
  });

  return (
    <>
      <Modal
        aria-labelledby="modal-my-account"
        aria-describedby="modal-my-account-description"
        open={isOpen}
      >
        <Box
          sx={{
            backgroundColor: 'primary.main',
            height: '100vh',
            pt: mdUp ? 0 : 4,
          }}
        >
          <Box
            sx={{
              height: '100%',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px 12px 0 0',
            }}
          >
            <Box
              sx={{
                maxWidth: theme.breakpoints.values.md,
                mx: 'auto',
                p: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton onClick={() => setIsOpen(false)} sx={{ zIndex: 2 }}>
                <KeyboardBackspace sx={{ color: '#000000' }} />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                Confirmar endereço
              </Typography>
            </Box>
            {mdUp && <Divider />}
            <Box
              sx={{
                margin: '0 auto',
                padding: mdUp ? '16px 28px' : '0 28px',
                maxWidth: theme.breakpoints.values.md,
              }}
            >
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Formik
              initialValues={{
                zipcode: newDataAddress.zipcode,
                neighbourhood: newDataAddress.neighbourhood,
                street: newDataAddress.street,
                city: `${newDataAddress.city} - ${newDataAddress.state}`,
                number: '',
                complement: '',
              }}
              validationSchema={formValidationSchema}
              onSubmit={handleValidateAddress}
              buttonText="Concluir"
            >
              <Box
                component={Form}
                sx={{
                  display: 'grid',
                  gridTemplateRows: `1fr ${mdUp ? '.7fr' : 'auto'}`,
                  gap: mdUp ? '50px' : '0px',
                  height: 'calc(100% - 40px - 32px - 6px)',
                  maxWidth: theme.breakpoints.values.md,
                  margin: '0 auto',
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    alignSelf: mdUp ? 'end' : 'center',
                  }}
                >
                  <Typography
                    sx={{ mb: 2, fontWeight: '700' }}
                    color="#808080"
                    variant="h5"
                  >
                    Confira os dados antes de concluir
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        name="zipcode"
                        label="CEP"
                        placeholder="CEP"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        name="neighbourhood"
                        label="Bairro"
                        placeholder="Bairro"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        name="street"
                        label="Endereço"
                        placeholder="Endereço"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        name="city"
                        label="Cidade"
                        placeholder="Cidade"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        name="number"
                        label="Número"
                        placeholder="Número"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        name="complement"
                        label="Complemento"
                        placeholder="Complemento"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box
                  sx={{
                    gridRowStart: 2,
                    gridRowEnd: 3,
                    width: '100%',
                    maxWidth: '420px',
                    mx: 'auto',
                    mb: '38px',
                  }}
                >
                  <Button variant="contained" fullWidth type="submit">
                    concluir
                  </Button>
                </Box>
              </Box>
            </Formik>
          </Box>
        </Box>
      </Modal>

      <ModalDefault
        isOpen={openModalConfirmAddressSuccess}
        onClose={handleCloseModalSuccess}
        src={DefaultSuccess}
        alt="Imagem de endereço confirmado com sucesso"
        title="Endereço confirmado com sucesso!"
      />
    </>
  );
}

export type { IConfirmNewAddressProps };
